import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import SaleListService from "../../services/SaleListService";

const currency = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" });
const fmtMoney = (v) => currency.format(v ?? 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleString("tr-TR") : "");

export default function SaleDetailDialog({ saleId, visible, onHide }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (visible && saleId) {
            loadSaleDetail();
        }
    }, [visible, saleId]);

    const loadSaleDetail = async () => {
        try {
            setLoading(true);
            const res = await SaleListService.getSaleDetail(saleId);
            setData(res);
        } catch (error) {
            console.error("Satış detayları alınamadı:", error);
        } finally {
            setLoading(false);
        }
    };

    const footer = (
        <div className="flex justify-end">
            <Button label="Kapat" icon="pi pi-times" className="p-button-text" onClick={onHide} />
        </div>
    );

    return (
        <Dialog
            header={
                <div className="flex items-center gap-2">
                    <i className="pi pi-info-circle text-emerald-600 text-xl"></i>
                    <div>
                        <span className="font-bold">Satış Detayı</span>
                        {data && <span className="text-sm text-slate-500 ml-2">#{data.saleNo}</span>}
                    </div>
                </div>
            }
            visible={visible}
            onHide={onHide}
            style={{ width: "900px", maxWidth: "95vw" }}
            footer={footer}
            modal
            className="p-fluid"
        >
            {loading ? (
                <div className="flex justify-center p-8">
                    <i className="pi pi-spin pi-spinner text-4xl text-emerald-600"></i>
                </div>
            ) : data ? (
                <div className="space-y-6">
                    {/* Üst Bilgiler */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                        <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Müşteri</label>
                            <div className="font-bold text-slate-700">{data.customerName || "Perakende"}</div>
                        </div>
                        <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Satış Tarihi</label>
                            <div className="font-bold text-slate-700">{fmtDate(data.saleDate)}</div>
                        </div>
                        <div>
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Durum</label>
                            <Tag
                                value={data.balanceTotal <= 0 ? "Ödendi" : "Borçlu"}
                                severity={data.balanceTotal <= 0 ? "success" : "danger"}
                                rounded
                            />
                        </div>
                    </div>

                    {/* Ürünler Tablosu */}
                    <div>
                        <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-3 flex items-center gap-2">
                            <i className="pi pi-box text-emerald-600"></i> Satılan Ürünler
                        </h3>
                        <DataTable value={data.items} className="p-datatable-sm" stripedRows size="small">
                            <Column
                                field="productName"
                                header="ÜRÜN"
                                body={(row) => (
                                    <div className="flex flex-col">
                                        <span className="font-bold">{row.productName}</span>
                                        <span className="text-[10px] text-slate-400 font-medium">{row.warehouseName}</span>
                                    </div>
                                )}
                            />
                            <Column field="quantity" header="ADET" className="text-center" style={{ width: '80px' }} />
                            <Column
                                field="dropFromStock"
                                header="STOK"
                                className="text-center"
                                body={(r) => (
                                    <i className={`pi ${r.dropFromStock !== false ? 'pi-check-circle text-emerald-500' : 'pi-times-circle text-red-400'}`}></i>
                                )}
                                style={{ width: '60px' }}
                            />
                            <Column field="unitPrice" header="B. FİYAT" body={(r) => fmtMoney(r.unitPrice)} className="text-right" />
                            <Column field="lineTotal" header="TOPLAM" body={(r) => <span className="font-bold">{fmtMoney(r.lineTotal)}</span>} className="text-right" />
                        </DataTable>
                    </div>

                    {/* Ödemeler Tablosu */}
                    {data.payments && data.payments.length > 0 && (
                        <div>
                            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-3 flex items-center gap-2">
                                <i className="pi pi-wallet text-blue-600"></i> Tahsilat Geçmişi
                            </h3>
                            <DataTable value={data.payments} className="p-datatable-sm" stripedRows size="small">
                                <Column field="method" header="ÖDEME YÖNTEMİ" body={(r) => <Tag value={r.method} severity="info" />} />
                                <Column field="amount" header="TUTAR" body={(r) => <span className="font-bold text-emerald-600">{fmtMoney(r.amount)}</span>} className="text-right" />
                                <Column field="description" header="AÇIKLAMA" />
                            </DataTable>
                        </div>
                    )}

                    {/* Özet Alanı */}
                    <div className="flex flex-col items-end gap-2 border-t border-slate-200 pt-4">
                        <div className="flex justify-between w-full md:w-64">
                            <span className="text-slate-500 font-bold">Ara Toplam:</span>
                            <span className="font-bold">{fmtMoney(data.subTotal)}</span>
                        </div>
                        {data.discountTotal > 0 && (
                            <div className="flex justify-between w-full md:w-64 text-red-500">
                                <span className="font-bold">İndirim:</span>
                                <span className="font-bold">-{fmtMoney(data.discountTotal)}</span>
                            </div>
                        )}
                        <div className="flex justify-between w-full md:w-64 text-lg">
                            <span className="text-slate-900 font-black">GENEL TOPLAM:</span>
                            <span className="font-black text-slate-900">{fmtMoney(data.grandTotal)}</span>
                        </div>
                        <div className="flex justify-between w-full md:w-64 text-emerald-600">
                            <span className="font-bold uppercase text-xs">Toplam Tahsilat:</span>
                            <span className="font-bold">{fmtMoney(data.paidTotal)}</span>
                        </div>
                        <div className="flex justify-between w-full md:w-64 border-t border-slate-100 pt-2">
                            <span className="text-slate-500 font-bold uppercase text-xs">Kalan Borç:</span>
                            <span className={`font-black ${data.balanceTotal > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                                {fmtMoney(data.balanceTotal)}
                            </span>
                        </div>
                    </div>

                    {data.saleNote && (
                        <div className="bg-amber-50 p-3 rounded-lg border border-amber-100 italic text-sm text-amber-800">
                            <strong>Not:</strong> {data.saleNote}
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center p-8 text-slate-500">Detay bulunamadı.</div>
            )}
        </Dialog>
    );
}
